import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { fetchGallery } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

interface GalleryAlbum {
  id: number;
  title: string;
  date: string;
  images: number;
  thumbnail: string; // Can be a full URL or Unsplash key
}

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState<GalleryAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const getGallery = async () => {
      try {
        const response = await fetchGallery(); // Replace with your actual API endpoint
        const data = response.data;
        console.log(data)

        // Transform API response if needed
        const formatted = data.map((item: any) => ({
          id: item.id,
          title: item.title,
          date: item.date,
          images: item.images || 0,
          thumbnail: item.thumbnail || 'https://res.cloudinary.com/dwrqyrm84/image/upload/q_auto,f_auto/t_center_logo/v1747853348/ulqyzwzdazyrxpderkzr.jpg', // Default Unsplash photo
        }));

        setGalleryItems(formatted);
      } catch (err) {
        console.error(err);
        setError('Failed to load gallery.');
      } finally {
        setLoading(false);
      }
    };

    getGallery();
  }, [toast]);

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Photo Gallery</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Capturing precious moments and memorable events in our diocese
          </p>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 animate-pulse">Loading gallery...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : galleryItems.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📷</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No photos yet</h3>
            <p className="text-gray-600">Check back soon for new photo albums.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryItems.map((album) => (
              <Link key={album.id} to={`/gallery/${album.id}`}>
                <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <CardContent className="p-0">
                    <div className="relative overflow-hidden rounded-t-lg">
                      <div
                        className="w-full h-64 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                        style={{
                          backgroundImage: album.thumbnail.startsWith('https')
                            ? `url('${album.thumbnail}')`
                            : `url('https://res.cloudinary.com/dwrqyrm84/image/upload/q_auto,f_auto/t_center_logo/v1747853348/ulqyzwzdazyrxpderkzr.jpg')`,
                        }}
                      ></div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="text-center">
                            <div className="text-3xl mb-2">📸</div>
                            <p className="font-semibold">View Album</p>
                          </div>
                        </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                        {album.images} photos
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                        {album.title}
                      </h3>
                      <p className="text-gray-600">
                        {new Date(album.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
